import { FC } from 'react'
import styled from 'styled-components'
import {
  LandingPage,
  LandingPageSectionPosition,
  LandingPageType
} from '@/network/graphql.g'
import { requestThrow401 } from '@/utils/graphQl'
import { withBaseServerSideProps } from '@/HOC/withBaseServerSideProps'
import { Box, Flex } from '@/components/atoms/Grid'
import BlackFridayHeader from '@/components/molecules/blackFriday/header'
import { getMainData } from '@/utils/mainDataHelper'
import BlackFridayManufacturerContainer from '@/components/molecules/blackFriday/manufacturerContainer'
import BlackFridayCategories from '@/components/molecules/blackFriday/categories'
import { validFor } from '@/theme/validFor'
import { H2 } from '@/components/atoms/typography'
import { Routes } from '@/core/routes'
import Link from '@/components/atoms/link'
import useTranslation from 'next-translate/useTranslation'

type BlackFridayProps = {
  landingPage: LandingPage
}

const BlackFriday: FC<BlackFridayProps> = ({ landingPage }) => {
  const blackFridaySections = landingPage.sections.map((section) => section)
  const blackFridayManufacturerSection = blackFridaySections.filter(
    (item) => item.products !== null
  )
  const { t } = useTranslation('common')
  return (
    <>
      <BlackFridayHeader landingPage={landingPage} />
      <MainWrapper flexDirection="column">
        <StyledFlex
          flexDirection={{ mobile: 'column', tablet: 'row', desktop: 'row' }}
          height={{ mobile: 'auto', tablet: '28%', desktop: '28%' }}
        >
          <BlackFridayCategories
            blackFridaySections={blackFridaySections}
            position={LandingPageSectionPosition.Center1}
          />
          <BlackFridayCategories
            blackFridaySections={blackFridaySections}
            position={LandingPageSectionPosition.Center2}
          />
          <BlackFridayCategories
            blackFridaySections={blackFridaySections}
            position={LandingPageSectionPosition.Center3}
          />
        </StyledFlex>
        <Box>
          <BlackFridayManufacturerContainer
            blackFridayManufacturerSection={blackFridayManufacturerSection}
          />
        </Box>
        <Flex flexDirection="column" mb={{ desktop: 20 }}>
          <StyledH2> {t('BlackFriday.giftBoxTitle')}</StyledH2>
          <Link href={Routes.gifts}>
            <GiftBanner />
          </Link>
        </Flex>
      </MainWrapper>
    </>
  )
}

export default BlackFriday

export const getServerSideProps = withBaseServerSideProps(
  async ({
    sdk,
    withMainNavigation,
    withStore
  }): Promise<GetServerSideProps<BlackFridayProps>> => {
    const { success, response } = await requestThrow401({
      method: sdk.getBlackFridayLandingPage,
      variables: {
        type: LandingPageType.BlackFriday,
        withMainNavigation,
        withStore
      },
      mainResponse: (response) => response?.landingPage
    })
    if (!success) {
      return {
        notFound: true
      }
    }

    return {
      props: {
        mainData: getMainData(response),
        landingPage: response?.landingPage,
        seoTags: response?.seoTags
      }
    }
  }
)

const MainWrapper = styled(Flex)`
  width: 100%;
`

const StyledFlex = styled(Flex)`
  align-items: center;
  justify-content: space-around;
  background-image: url('https://i.factcool.com/blackFriday/background_desktop.jpeg');
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  padding-bottom: 100px;
  padding-top: 60px;
  color: ${(props) => props.theme.colors.menuBackground};
  ${validFor.tablet`
   padding-bottom: 30px;
   padding-top: 30px; 
  `}
  ${validFor.mobile`
   background-image: url('https://i.factcool.com/blackFriday/background_mobile.jpeg');
   padding-bottom: 20px;
   padding-top: 20px; 
   background-position: right;
  `}
`

const GiftBanner = styled(Box)`
  background-image: url('https://i.factcool.com/blackFriday/Gift_Desktop.jpeg');
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  width: 100%;
  height: 290px;
  ${validFor.tablet`
    background-size: contain;
    margin-top: 0px;
    height: 200px;
  `}
  ${validFor.mobile`
   background-image: url('https://i.factcool.com/blackFriday/Gift_Mobile.jpeg');
   background-size: contain;
  `}
  &:hover {
    cursor: pointer;
  }
`
const StyledH2 = styled(H2)`
  text-align: center;
  ${validFor.mobile`
    margin-bottom: -20px;
  `}
  ${validFor.tablet`
    margin-bottom: -5px;
    font-size: 24px;
  `}
`
